import mixpanel from 'mixpanel-browser';
import { FrontendAnalyticsEventsType } from '@sc/analytics';
import env from '../environment';

const API_HOST = 'https://api-eu.mixpanel.com';

let initialised = false;

// Query strings carry single-use tokens (verify-email, magic links).
const stripQuery = (url: string): string => {
  try {
    const parsed = new URL(url);

    parsed.search = '';

    return parsed.toString();
  } catch {
    return url.split('?')[0] ?? url;
  }
};

type TrackOptionsType = {
  sendImmediately?: boolean;
};

const registerApp = (): void => mixpanel.register({ app: 'frontend' });

export const initAnalytics = (): void => {
  const token = env.mixpanel.token;

  if (!token || initialised) return;

  mixpanel.init(token, {
    autocapture: false,
    api_host: API_HOST,
  });

  initialised = true;

  registerApp();
};

export const trackPageView = (): void => {
  if (!initialised) return;

  // `$current_url` rides on every event, so keep it query-free.
  mixpanel.register({ $current_url: stripQuery(window.location.href) });

  mixpanel.track_pageview(
    { current_url_search: '' },
    { event_name: 'Page View' }
  );
};

export const track = <Name extends keyof FrontendAnalyticsEventsType>(
  name: Name,
  ...args: FrontendAnalyticsEventsType[Name] extends undefined
    ? [properties?: undefined, options?: TrackOptionsType]
    : [properties: FrontendAnalyticsEventsType[Name], options?: TrackOptionsType]
): void => {
  if (!initialised) return;

  const [properties, options] = args;

  mixpanel.track(
    name,
    properties,
    options?.sendImmediately ? { send_immediately: true } : {}
  );
};

/** Sets the profile properties too — the API's processor no longer does. */
export const identifyUser = ({
  email,
  name,
}: {
  email: string;
  name: string | null;
}): void => {
  if (!initialised) return;

  mixpanel.identify(email);

  mixpanel.people.set({ $email: email, ...(name ? { $name: name } : {}) });
};

export const resetAnalytics = (): void => {
  if (!initialised) return;

  mixpanel.reset();

  registerApp();
};
